/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
// HACK #1: to force Specrum child components to re-render when user clicks 'Clear Filters' -- we need to the selected state of the Spectrum radioGroup/checkboxes to resset, so we use this to force a RE-CONSTRUCT (not re-render! re-render does not update the value) that'll reset the value of the filter option. The 're-construct' happens b/c the KEY value changes -- check render()
import { Reducer } from 'react';
import { AnyAction } from 'redux';
import * as ActionTypes from '../actionTypes';
import { SEARCH_PAGES, SEARCH_TABS_CONSTANTS } from '../containers/SearchResultsPage/SearchConstants';
import { getSelectedFiltersForChips } from './reducerHelpers';
import SearchUtils from '../utils/SearchUtils';

const initialSearchResultsTotalCountByTab = {};

if (typeof SEARCH_TABS_CONSTANTS === 'object') {
	const tabList = Object.values(SEARCH_TABS_CONSTANTS);

	if (Array.isArray(tabList) && tabList.length > 0) {
		tabList.forEach((tab) => {
			if (typeof tab === 'string') {
				initialSearchResultsTotalCountByTab[tab] = null;
			}
		});
	}
}

export const initialState: SearchState = {
	searchResultsPayload: {},
	searchResultsMeta: {},
	searchResultsTrackingMetadata: {},
	searchAssetsResultsPayload: {},
	searchAssetsResultsMeta: '',
	searchHistoryItems: [],
	searchTrendingItems: {
		products: [],
		verbs: [],
		planGroup: [],
		modelVersion: '',
		isExpress: false,
		isEmpty: true,
	},
	searchFontResults: [],
	searchPredictLinks: [],
	filteredSearchDropdownItems: {
		leoOnboardingSummaryItems: [],
		searchRecommendations: [],
		searchHistory: [],
		searchAutoComplete: [],
		searchTrending: [],
		searchApps: [],
		intentShortcuts: [],
		files: [],
		recentFiles: [],
	},
	searchAutoCompleteItems: [],
	searchAutoCompleteTextSuggestions: [],
	searchAutoCompleteIntentTerms: [],
	searchAutoCompleteIntentShortcuts: [],
	searchAutoCompleteFiles: {
		all: [],
		matched: [],
		recent: [],
		query: '',
	},
	leoOnboardingSummaryItems: [],
	searchAutoCompleteLoadedAt: 0,
	searchAutoCompleteQuery: '',
	exactTypedSearchQuery: '',
	searchQuery: '',
	tempSearchQuery: '',
	previousSearchQuery: '',
	showSearchDropdown: false,
	resetThumbsFeedback: false,
	showSearchThumbsFeedback: true,
	currentSearchView: SEARCH_PAGES.ALL_RESULTS,
	submittedThumbsFeedback: false,
	submittedSearchQuery: false,
	localFilesResults: {},
	srpCurrentTab: -1,
	searchConfig: {},
	yourWorkFiltersOptions: [],
	yourWorkFiltersSelected: false,
	yourWorkFiltersApplied: false,
	yourWorkFiltersCleared: true,
	// ** reference HACK #1!
	yourWorkFiltersDisplay: true,
	theme: 'light',
	searchResultsTotalCountByTab: initialSearchResultsTotalCountByTab,
	searchDropdownCurrentSectionItem: '',
	yourWorkFilterChips: new Set<string>(),
	preSearchRecommendations: {},
	botSuggestions: [],
	presearchResultsPayload: {
		intentToAction: {},
		expressQuickActions: {},
		popularExpressTemplates: {},
		popularSearches: {
			attributes: {
				products: [],
				verbs: [],
				planGroup: [],
				modelVersion: '',
				isExpress: false,
				isEmpty: true,
			},
			searchResults: {
				products: [],
				verbs: [],
				planGroup: [],
				modelVersion: '',
				isExpress: false,
				isEmpty: true,
			},
		},
	},
	leftNavOn: false,
	sourceOfExecutedQuery: '',
	queryIntent: '',
};

export interface SearchAction extends AnyAction {
	payload?: any;
	meta?: any;
}

const searchReducer: Reducer<SearchState, SearchAction> = (state: SearchState = initialState, action): SearchState => {
	let searchResultsTotalCountByTab = state.searchResultsTotalCountByTab;

	switch (action.type) {
		case ActionTypes.RESET_SEARCH:
			return {
				...state,
				...action.payload,
			};

		case ActionTypes.UPDATE_LEFTNAV_STATUS:
			return Object.assign({}, state, {
				leftNavOn: action.payload,
			});

		case ActionTypes.SET_FONT_SEARCH_RESULTS: {
			const loading = action.loading;
			const fontItemIndex = state?.searchResultsPayload?.data?.findIndex((v) => v.id === 'fonts');
			const fontItem = { ...state?.searchResultsPayload?.data?.[fontItemIndex], loading };
			const fontDataResults = action.payload ?? [];
			if (loading) {
				state.searchResultsPayload.data.splice(fontItemIndex, 1, fontItem);
			} else if (!loading && fontItem) {
				fontItem.searchResults.resultItems = fontDataResults;
				fontItem.searchResults.totalResults = fontDataResults.length;
				state.searchResultsPayload.data.splice(fontItemIndex, 1, fontItem);
			}
			return Object.assign({}, state, {
				searchResultsPayload: { ...state.searchResultsPayload },
			});
		}

		case ActionTypes.FETCH_SEARCH_RESULTS: {
			if (action?.payload?.category) {
				const { category, ...payload } = action.payload;
				const categoryResult: SearchCategoryResult = SearchUtils.getSearchCategoryData(payload.data, category);
				if (categoryResult) {
					const allCategoriesPayload = { ...state.searchResultsPayload };
					allCategoriesPayload.data.forEach((categoryPayload: SearchCategoryResult) => {
						if (categoryPayload?.id?.toLowerCase() === category?.toLowerCase()) {
							categoryPayload.metadata = categoryResult.metadata;
							categoryPayload?.appArray?.push(...(categoryResult?.appArray ?? []));
							const categoryResultItems = [
								...categoryPayload.searchResults.resultItems,
								...categoryResult.searchResults.resultItems,
							];
							categoryPayload.searchResults = categoryResult.searchResults;
							categoryPayload.searchResults.resultItems = categoryResultItems;
						}
					});

					return Object.assign({}, state, {
						searchResultsPayload: allCategoriesPayload,
						searchResultsMeta: action.meta,
					});
				}
			}
			return Object.assign({}, state, {
				searchResultsPayload: action.payload,
				searchResultsMeta: action.meta,
			});
		}

		case ActionTypes.FETCH_PRESEARCH_RESULTS:
			return Object.assign({}, state, {
				presearchResultsPayload: Object.assign(
					initialState.presearchResultsPayload,
					action.payload?.data || {},
				),
			});
		case ActionTypes.FETCH_SEARCH_ASSETS_RESULTS:
			return Object.assign({}, state, {
				searchAssetsResultsPayload: action.payload,
				searchAssetsResultsMeta: action.meta,
			});

		case ActionTypes.FETCH_SEARCH_HISTORY:
			return Object.assign({}, state, {
				searchHistoryItems: action.payload,
			});

		case ActionTypes.FETCH_SEARCH_APP_METADATA:
			return Object.assign({}, state, {
				searchAppMetadataList: action.payload,
			});

		case ActionTypes.FETCH_ML_SEARCH_LINKS:
			return Object.assign({}, state, {
				searchPredictLinks: action.payload,
			});

		case ActionTypes.SET_SEARCH_HISTORY:
			return Object.assign({}, state, {
				searchHistoryItems: action.payload.map((item) => item),
			});

		case ActionTypes.FETCH_SEARCH_AUTO_SUGGESTION:
			if (action.payload.searchAutoCompleteLoadedAt >= state.searchAutoCompleteLoadedAt) {
				return Object.assign({}, state, {
					searchAutoCompleteItems: action.payload.searchAutoCompleteItems,
					searchAutoCompleteTextSuggestions: action.payload.searchAutoCompleteTextSuggestions,
					searchAutoCompleteLoadedAt: action.payload.searchAutoCompleteLoadedAt,
					searchAutoCompleteQuery: action.payload.searchAutoCompleteQuery,
				});
			}
			return state;

		case ActionTypes.FETCH_SEARCH_GEN_SUMMARY:
			return Object.assign({}, state, {
				leoOnboardingSummaryItems: action.payload.leoOnboardingSummaryItems,
			});
		case ActionTypes.FETCH_USER_PREFERENCES:
			return Object.assign({}, state, {
				showGenAIResponses: action.payload.showGenAIResponses,
			});
		case ActionTypes.STORE_USER_PREFERENCES:
			return Object.assign({}, state, {
				storingUserPreferences: action.payload.storingUserPreferences,
				showGenAIResponses: action.payload.showGenAIResponses,
			});
		case ActionTypes.FETCH_SEARCH_GEN_SUMMARY_IN_PROGRESS:
			return Object.assign({}, state, {
				fetchingGenAiResult: action.payload.fetchingGenAiResult,
			});
		case ActionTypes.FETCH_INTENT_SHORTCUT_SUGGESTION:
			return {
				...state,
				searchAutoCompleteIntentShortcuts: action.payload.searchAutoCompleteIntentShortcuts,
				searchAutoCompleteIntentTerms: action.payload.searchAutoCompleteIntentTerms,
			};

		case ActionTypes.FETCH_RECENT_FILES:
			return {
				...state,
				searchAutoCompleteFiles: {
					...state.searchAutoCompleteFiles,
					...action.payload,
				},
			};

		case ActionTypes.FETCH_SAYT_FILES: {
			const searchQueryChangedSinceRequest = state.searchQuery !== action.payload.query;
			if (!searchQueryChangedSinceRequest) {
				return {
					...state,
					searchAutoCompleteFiles: {
						...state.searchAutoCompleteFiles,
						...action.payload,
					},
				};
			}
			return state;
		}

		case ActionTypes.FILTER_SEARCH_DROPDOWN: {
			const updatedFilteredSearchDropdownItems = {
				...state.filteredSearchDropdownItems,
				[action.payload.searchDropdownSection]: action.payload.filteredSearchDropdownItems,
			};

			return Object.assign({}, state, {
				filteredSearchDropdownItems: updatedFilteredSearchDropdownItems,
			});
		}

		case ActionTypes.SEARCH_QUERY:
			return Object.assign({}, state, {
				searchQuery: action.payload,
			});

		case ActionTypes.EXACT_TYPED_SEARCH_QUERY:
			return Object.assign({}, state, {
				exactTypedSearchQuery: action.payload,
			});

		case ActionTypes.TEMP_SEARCH_QUERY:
			return Object.assign({}, state, {
				tempSearchQuery: action.payload,
			});

		case ActionTypes.SET_PREVIOUS_SEARCH_QUERY:
			return Object.assign({}, state, {
				previousSearchQuery: action.payload,
			});

		case ActionTypes.SUBMITTED_SEARCH_QUERY:
			return Object.assign({}, state, {
				submittedSearchQuery: action.payload,
			});

		case ActionTypes.SHOW_SEARCH_DROPDOWN:
			return Object.assign({}, state, {
				showSearchDropdown: action.payload,
			});

		case ActionTypes.RESET_THUMBS_FEEDBACK:
			return Object.assign({}, state, {
				resetThumbsFeedback: action.payload,
			});

		case ActionTypes.SHOW_SEARCH_THUMBS_FEEDBACK:
			return Object.assign({}, state, {
				showSearchThumbsFeedback: action.payload,
			});

		case ActionTypes.CURRENT_SEARCH_VIEW:
			return Object.assign({}, state, {
				currentSearchView: action.payload,
			});

		case ActionTypes.SUBMITTED_THUMBS_FEEDBACK:
			return Object.assign({}, state, {
				submittedThumbsFeedback: action.payload,
			});

		case ActionTypes.SET_SRP_CURRENT_TAB:
			return Object.assign({}, state, {
				srpCurrentTab: action.payload,
			});

		case ActionTypes.SET_SEARCH_GLOBAL_CONFIG:
			return Object.assign({}, state, {
				searchConfig: action.payload,
			});

		case ActionTypes.SET_YOUR_WORK_FILTERS:
			return Object.assign({}, state, {
				yourWorkFiltersOptions: action.payload,
			});

		case ActionTypes.SET_YOUR_WORK_FILTERS_SELECTED:
			return Object.assign({}, state, {
				yourWorkFiltersSelected: action.payload,
			});

		case ActionTypes.SET_YOUR_WORK_FILTERS_APPLIED:
			return Object.assign({}, state, {
				yourWorkFiltersApplied: action.payload,
			});

		case ActionTypes.SET_YOUR_WORK_FILTERS_CLEARED:
			return Object.assign({}, state, {
				yourWorkFiltersCleared: action.payload,
			});

		case ActionTypes.SET_YOUR_WORK_FILTERS_DISPLAY:
			return Object.assign({}, state, {
				yourWorkFiltersDisplay: action.payload,
			});

		case ActionTypes.SET_THEME:
			return Object.assign({}, state, {
				theme: action.payload,
			});

		case ActionTypes.SET_SEARCH_RESULTS_TOTAL_COUNT_BY_TAB:
			if (searchResultsTotalCountByTab && action.payload.tab) {
				if (action.payload.tab === 'resetTotalCount') {
					const initialSearchResultsTotalCountByTab = {};

					if (typeof SEARCH_TABS_CONSTANTS === 'object') {
						const tabList = Object.values(SEARCH_TABS_CONSTANTS);

						if (Array.isArray(tabList) && tabList.length > 0) {
							tabList.forEach((tab) => {
								if (typeof tab === 'string') {
									initialSearchResultsTotalCountByTab[tab] = null;
								}
							});
						}
					}

					searchResultsTotalCountByTab = initialSearchResultsTotalCountByTab;
				} else {
					searchResultsTotalCountByTab[action.payload.tab] = action.payload.totalCount;
				}
			}

			return Object.assign({}, state, {
				searchResultsTotalCountByTab,
			});

		case ActionTypes.SET_SEARCH_DROPDOWN_CURRENT_SECTION_ITEM:
			return Object.assign({}, state, {
				searchDropdownCurrentSectionItem: action.payload,
			});

		case ActionTypes.SET_YOUR_WORK_FILTER_CHIPS:
			return Object.assign({}, state, {
				yourWorkFilterChips: getSelectedFiltersForChips(state.yourWorkFiltersOptions),
			});
		case ActionTypes.UPDATE_SELECTED_FILTER_OPTIONS:
			return Object.assign({}, state, {
				yourWorkFilterOptions: state.yourWorkFiltersOptions.splice(
					action.payload.index,
					1,
					action.payload.filterOption,
				),
			});
		case ActionTypes.UPDATE_PRE_SEARCH_RECOMMENDATIONS:
			return Object.assign({}, state, {
				preSearchRecommendations: action.payload.recommendations,
			});
		case ActionTypes.UPDATE_BOT_SUGGESTIONS:
			return {
				...state,
				botSuggestions: action.payload.botSuggestions,
			};
		case ActionTypes.SET_SOURCE_OF_QUERY:
			return Object.assign({}, state, {
				sourceOfExecutedQuery: action.payload,
			});
		case ActionTypes.SET_QUERY_INTENT:
			return Object.assign({}, state, {
				queryIntent: action.payload,
			});
		default:
			return state;
	}
};

export default searchReducer;
